/**
 * Sidebar Component
 *
 * Collapsible navigation sidebar with platform-aware behavior
 * - PWA: 0px (hidden) → 240px (full)
 * - Webapp: 80px (icons only) → 240px (full with labels)
 */

import { useNavigate, useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { isPWA } from "../lib/platform";
import { useAuth } from "../contexts/AuthContext";
import logoImage from "../assets/svg/logo.svg";
import logoDark from "../assets/svg/logo_dark.svg";
import burgerIcon from "../assets/svg/burger.svg";
import chatIcon from "../assets/svg/chat.svg";
import feedbackIcon from "../assets/svg/feedback.svg";
import settingIcon from "../assets/svg/setting.svg";
import faqIcon from "../assets/svg/faqSvg.svg";
import { ExtensionSidebarButton } from "./ExtensionSidebarButton";

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isInPWA = isPWA();
  const { user, logout } = useAuth();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  // Helper function to get user initials for avatar
  const getUserInitials = (name?: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLogout = () => {
    if (confirm("Are you sure you want to logout?")) {
      logout();
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showProfileMenu]);

  const navItems = [
    { id: "chat", icon: chatIcon, label: "Chat", path: "/chat" },
    // {
    //   id: "incidents",
    //   icon: incidentIcon,
    //   label: "Incidents",
    //   path: "/dashboard",
    // },
    // { id: "notes", icon: noteIcon, label: "Notes", path: "/notes" },
    // { id: "links", icon: feedbackIcon, label: "Links", path: "/links" },
    // { id: "autopilot", icon: null, label: "Autopilot", isToggle: true },
  ];

  const bottomItems = [
    // {
    //   id: "notifications",
    //   icon: notificationIcon,
    //   label: "Notifications",
    //   path: "/notifications",
    // },
    { id: "settings", icon: settingIcon, label: "Settings", path: "/settings" },
    {
      id: "faq",
      icon: faqIcon,
      label: "FAQ",
      path: "/faq",
    },
    {
      id: "feedback",
      icon: feedbackIcon,
      label: "Feedback",
      path: "/feedback",
    },
  ];

  const handleNavigation = (path?: string) => {
    if (path) {
      navigate(path);
    }
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path;
  };

  return (
    <>
      {/* Overlay for PWA when sidebar is open */}
      {isInPWA && isOpen && (
        <div className="fixed inset-0 bg-black/20 z-40" onClick={onToggle} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-[#FDFAF7] dark:bg-[#122135] border-r border-[#E5DDD3] dark:border-[#2C405F] transition-all duration-300 ease-out ${
          isInPWA
            ? `w-64 z-50 ${isOpen ? "translate-x-0" : "-translate-x-full"}`
            : `z-30 ${isOpen ? "w-64" : "w-16"}`
        }`}
      >
        <div
          className={`flex flex-col h-full overflow-hidden ${
            isOpen ? "w-64" : isInPWA ? "w-64" : "w-16"
          }`}
        >
          {/* Header - different for PWA vs Webapp */}
          {isInPWA ? (
            /* PWA: Logo + Title + Close Button */
            <div className="flex items-center justify-between p-3 h-14 border-b border-[#E5DDD3] dark:border-[#2C405F]">
              <div className="flex items-center gap-2">
                <img
                  src={logoImage}
                  alt="DuneWorks"
                  className="w-6 h-6 flex-shrink-0 dark:hidden"
                />
                <img
                  src={logoDark}
                  alt="DuneWorks"
                  className="w-6 h-6 flex-shrink-0 hidden dark:block"
                />
                <span className="text-[#1D1B19] dark:text-[#E3E6EA] font-semibold whitespace-nowrap">
                  DuneWorks
                </span>
              </div>
              <button
                onClick={onToggle}
                className="p-2 hover:bg-[#F5E9D7] dark:hover:bg-[#2C405F] rounded-lg transition-colors flex-shrink-0"
              >
                <svg
                  className="w-5 h-5 text-[#1D1B19] dark:text-[#F3F4F6]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ) : isOpen ? (
            /* Webapp Expanded: Logo on left + Burger close on right */
            <div className="flex items-center justify-between pl-6 pr-3 h-14 border-b border-[#E5DDD3] dark:border-[#2C405F]">
              <div className="flex items-center">
                <img
                  src={logoImage}
                  alt="DuneWorks"
                  className="w-5 h-5 flex-shrink-0 dark:hidden"
                />
                <img
                  src={logoDark}
                  alt="DuneWorks"
                  className="w-5 h-5 flex-shrink-0 hidden dark:block"
                />
              </div>
              <button
                onClick={onToggle}
                className="p-2 hover:bg-[#F5E9D7] dark:hover:bg-[#2C405F] rounded-lg transition-colors flex-shrink-0"
              >
                <img
                  src={burgerIcon}
                  alt="Close"
                  className="w-5 h-5 dark:[filter:brightness(0)_saturate(100%)_invert(98%)_sepia(2%)_saturate(1086%)_hue-rotate(185deg)_brightness(98%)_contrast(93%)]"
                />
              </button>
            </div>
          ) : (
            /* Webapp Collapsed: Logo/Burger toggle button centered */
            <div className="flex items-center justify-center h-14 border-b border-[#E5DDD3] dark:border-[#2C405F]">
              <button
                onClick={onToggle}
                className="p-2 hover:bg-[#F5E9D7] dark:hover:bg-[#2C405F] rounded-lg transition-colors flex-shrink-0 group"
              >
                {/* Show logo by default, burger on hover */}
                <img
                  src={logoImage}
                  alt="Menu"
                  className="w-5 h-5 group-hover:hidden dark:hidden"
                />
                <img
                  src={logoDark}
                  alt="Menu"
                  className="w-5 h-5 group-hover:hidden hidden dark:block"
                />
                <img
                  src={burgerIcon}
                  alt="Menu"
                  className="w-5 h-5 hidden group-hover:block dark:[filter:brightness(0)_saturate(100%)_invert(98%)_sepia(2%)_saturate(1086%)_hue-rotate(185deg)_brightness(98%)_contrast(93%)]"
                />
              </button>
            </div>
          )}

          {/* Main Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center ${
                  isOpen ? "gap-3" : "justify-center"
                } p-3 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? "bg-[#E5DDD3] dark:bg-[#C6CED8]"
                    : "hover:bg-[#F5E9D7] dark:hover:bg-[#2C405F]"
                }`}
              >
                {item.icon && (
                  <img
                    src={item.icon}
                    alt={item.label}
                    className={`w-5 h-5 flex-shrink-0 ${
                      !isActive(item.path)
                        ? "dark:[filter:brightness(0)_saturate(100%)_invert(98%)_sepia(2%)_saturate(1086%)_hue-rotate(185deg)_brightness(98%)_contrast(93%)]"
                        : ""
                    }`}
                  />
                )}
                {isOpen && (
                  <span
                    className={`text-sm font-normal whitespace-nowrap ${
                      isActive(item.path)
                        ? "text-[#1D1B19] dark:text-[#0A1423]"
                        : "text-[#1D1B19] dark:text-[#F3F4F6]"
                    }`}
                  >
                    {item.label}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* Chrome Extension Button */}
          <ExtensionSidebarButton isOpen={isOpen} />

          {/* Bottom Navigation - Fixed to bottom */}
          <div className="mt-auto p-3 space-y-1 border-t border-[#E5DDD3] dark:border-[#2C405F]">
            {bottomItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center ${
                  isOpen ? "gap-3" : "justify-center"
                } p-3 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? "bg-[#E5DDD3] dark:bg-[#2C405F]"
                    : "hover:bg-[#F5E9D7] dark:hover:bg-[#2C405F]"
                }`}
              >
                <img
                  src={item.icon}
                  alt={item.label}
                  className={`w-5 h-5 flex-shrink-0 ${
                    !isActive(item.path)
                      ? "dark:[filter:brightness(0)_saturate(100%)_invert(98%)_sepia(2%)_saturate(1086%)_hue-rotate(185deg)_brightness(98%)_contrast(93%)]"
                      : ""
                  }`}
                />
                {isOpen && (
                  <span
                    className={`text-sm font-normal whitespace-nowrap ${
                      isActive(item.path)
                        ? "text-[#1D1B19] dark:text-[#E3E6EA]"
                        : "text-[#1D1B19] dark:text-[#F3F4F6]"
                    }`}
                  >
                    {item.label}
                  </span>
                )}
              </button>
            ))}

            {/* User Profile Button with Popup Menu */}
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className={`w-full flex items-center ${
                  isOpen ? "gap-3" : "justify-center"
                } p-3 rounded-lg transition-colors hover:bg-[#F5E9D7] dark:hover:bg-[#2C405F]`}
              >
                {/* User Avatar */}
                <div className="w-8 h-8 flex-shrink-0">
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-full h-full rounded-full object-cover"
                      style={{ borderRadius: "50%" }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        const fallback =
                          target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div
                    className="w-full h-full rounded-full bg-gradient-to-br from-[#2C405F] to-[#6E829E] dark:from-[#6E829E] dark:to-[#9AAABF] flex items-center justify-center"
                    style={{
                      borderRadius: "50%",
                      display: user?.picture ? "none" : "flex",
                    }}
                  >
                    <span className="text-white text-sm font-semibold">
                      {getUserInitials(user?.name)}
                    </span>
                  </div>
                </div>

                {isOpen && (
                  <div className="flex flex-col items-start flex-1 min-w-0">
                    <span className="text-sm font-medium text-[#1D1B19] dark:text-[#E3E6EA] truncate w-full">
                      {user?.name || "User"}
                    </span>
                    <span className="text-xs text-[#8E8378] dark:text-[#9AAABF] truncate w-full">
                      {user?.email || ""}
                    </span>
                  </div>
                )}

                {isOpen && (
                  <svg
                    className="w-4 h-4 text-[#8E8378] dark:text-[#9AAABF] flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
                    />
                  </svg>
                )}
              </button>

              {/* Popup Menu - ChatGPT Style */}
              {showProfileMenu && (
                <div
                  className={`fixed bg-white dark:bg-[#2C405F] rounded-xl shadow-2xl border border-[#E5DDD3] dark:border-[#455D7D] overflow-hidden z-[100] ${
                    isOpen
                      ? "absolute bottom-full left-3 right-3 mb-2"
                      : "left-20 bottom-3 w-48"
                  }`}
                >
                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      handleNavigation("/profile");
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F5EFE6] dark:hover:bg-[#455D7D] transition-colors"
                  >
                    <svg
                      className="w-5 h-5 text-[#2C405F] dark:text-[#9AAABF]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    <span className="text-sm font-medium text-[#1D1B19] dark:text-[#E3E6EA]">
                      View Profile
                    </span>
                  </button>

                  <div className="h-[1px] bg-[#E5DDD3] dark:bg-[#455D7D]" />

                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      handleLogout();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <svg
                      className="w-5 h-5 text-red-600 dark:text-red-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                    <span className="text-sm font-medium text-red-600 dark:text-red-400">
                      Logout
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
